import { useState } from 'react'
import { t } from '@/i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createReportDefinition,
  getReportDefinitions,
  getReportRuns,
  reportFileUrl,
  runMonthlyReport,
  runReport,
  type ReportRunOut,
} from '@/lib/api'
import { isAppShell } from '@/lib/app-shell'
import { formatDateTime } from '@/lib/format'

const KIND_IDS = ['custom', 'tax_fifo', 'profit_loss', 'transactions', 'bank_flow', 'reconciliation', 'counterparties'] as const

const KIND_KEY: Record<string, string> = {
  custom: 'rep.custom',
  tax_fifo: 'rep.tax',
  profit_loss: 'rep.pnl',
  transactions: 'rep.deals',
  bank_flow: 'rep.flow',
  reconciliation: 'rep.recon',
  counterparties: 'rep.cp',
}

function kindLabel(kind: string): string {
  const key = KIND_KEY[kind]
  return key ? t(key) : kind
}

export function Reports() {
  const queryClient = useQueryClient()
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [format, setFormat] = useState<'xlsx' | 'pdf' | 'csv'>('xlsx')
  const [kind, setKind] = useState('tax_fifo')
  const [fromDate, setFromDate] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`)
  const [toDate, setToDate] = useState(now.toISOString().slice(0, 10))
  const [saveName, setSaveName] = useState('')

  const definitions = useQuery({ queryKey: ['reports', 'definitions'], queryFn: getReportDefinitions })
  const runs = useQuery({ queryKey: ['reports', 'runs'], queryFn: getReportRuns })

  const monthly = useMutation({
    mutationFn: () => runMonthlyReport(year, month, format),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: ['reports'] })
      openFile(row)
    },
  })
  const custom = useMutation({
    mutationFn: () =>
      runReport({
        kind,
        format,
        from_at: new Date(`${fromDate}T00:00:00`).toISOString(),
        to_at: new Date(`${toDate}T23:59:59`).toISOString(),
        params: kind === 'custom' ? { pack: 'monthly' } : {},
      }),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: ['reports'] })
      openFile(row)
    },
  })
  const fromTemplate = useMutation({
    mutationFn: (definitionId: string) =>
      runReport({
        definition_id: definitionId,
        format,
        from_at: new Date(`${fromDate}T00:00:00`).toISOString(),
        to_at: new Date(`${toDate}T23:59:59`).toISOString(),
      }),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: ['reports'] })
      openFile(row)
    },
  })
  const save = useMutation({
    mutationFn: () =>
      createReportDefinition({
        name: saveName,
        kind,
        default_format: format,
        params: kind === 'custom' ? { pack: 'monthly' } : {},
      }),
    onSuccess: () => {
      setSaveName('')
      void queryClient.invalidateQueries({ queryKey: ['reports', 'definitions'] })
    },
  })

  const appShell = isAppShell()

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 md:px-8 md:py-8">
      <div>
        <h1 className="text-xl font-semibold text-slate-100">{t('rep.title')}</h1>
        <p className="mt-1 text-sm text-slate-500">{appShell ? t('rep.app') : t('rep.lead')}</p>
      </div>

      {appShell ? null : (
      <>
      <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-200">{t('rep.month')}</h2>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-500">
            {t('rep.year')}
            <input
              type="number"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
              className="mt-1 block w-24 rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs text-slate-500">
            {t('rep.monthNum')}
            <input
              type="number"
              min={1}
              max={12}
              value={month}
              onChange={(event) => setMonth(Number(event.target.value))}
              className="mt-1 block w-20 rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
            />
          </label>
          <FormatSelect value={format} onChange={setFormat} />
          <button
            type="button"
            className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
            disabled={monthly.isPending}
            onClick={() => monthly.mutate()}
          >
            {monthly.isPending ? t('rep.making') : t('rep.makeMonth')}
          </button>
        </div>
        {monthly.isError && (
          <p className="mt-2 text-sm text-loss">{monthly.error instanceof Error ? monthly.error.message : t('common.error')}</p>
        )}
      </section>

      <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-200">{t('rep.builder')}</h2>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-500">
            {t('rep.kind')}
            <select
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              className="mt-1 block rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
            >
              {KIND_IDS.map((id) => (
                <option key={id} value={id}>
                  {kindLabel(id)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-500">
            {t('rep.from')}
            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              className="mt-1 block rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs text-slate-500">
            {t('rep.to')}
            <input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              className="mt-1 block rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
            />
          </label>
          <FormatSelect value={format} onChange={setFormat} />
          <button
            type="button"
            className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
            disabled={custom.isPending}
            onClick={() => custom.mutate()}
          >
            {custom.isPending ? t('rep.making') : t('rep.make')}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input
            value={saveName}
            onChange={(event) => setSaveName(event.target.value)}
            placeholder={t('rep.tplName')}
            className="w-64 rounded-md border border-surface-border bg-surface px-3 py-1.5 text-sm"
          />
          <button
            type="button"
            className="rounded-md border border-surface-border px-3 py-2 text-sm text-slate-300 disabled:opacity-50"
            disabled={!saveName.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {t('rep.saveTpl')}
          </button>
        </div>
        {(custom.isError || save.isError) && (
          <p className="mt-2 text-sm text-loss">
            {custom.error instanceof Error
              ? custom.error.message
              : save.error instanceof Error
                ? save.error.message
                : t('common.error')}
          </p>
        )}
      </section>
      </>
      )}

      <section className="mt-6">
        <h2 className="text-sm font-medium text-slate-300">{t('rep.saved')}</h2>
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {(definitions.data ?? []).map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 rounded-md border border-surface-border px-3 py-2">
              <div>
                <div className="text-sm text-slate-200">{item.name}</div>
                <div className="text-xs text-slate-500">{item.description ?? kindLabel(item.kind)}</div>
              </div>
              {appShell ? null : (
                <button
                  type="button"
                  className="text-sm text-slate-300 hover:text-white"
                  onClick={() => fromTemplate.mutate(item.id)}
                >
                  {t('rep.run')}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-sm font-medium text-slate-300">{t('rep.latest')}</h2>
        {runs.data && runs.data.length === 0 && <p className="mt-3 text-sm text-slate-500">{t('rep.empty')}</p>}
        {runs.data && runs.data.length > 0 && (
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs text-slate-500">
              <tr>
                <th className="py-1">{t('rep.when')}</th>
                <th className="py-1">{t('rep.kind')}</th>
                <th className="py-1">{t('rep.period')}</th>
                <th className="py-1">{t('rep.format')}</th>
                <th className="py-1">{t('rep.rows')}</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {runs.data.map((row) => (
                <tr key={row.id} className="border-t border-surface-border/60">
                  <td className="py-2 text-slate-400">{formatDateTime(row.created_at)}</td>
                  <td className="py-2">{kindLabel(row.kind)}</td>
                  <td className="num py-2 text-slate-400">
                    {row.period_from} — {row.period_to}
                  </td>
                  <td className="py-2 uppercase text-slate-500">{row.format}</td>
                  <td className="num py-2">{row.row_count ?? '—'}</td>
                  <td className="py-2 text-right">
                    {appShell ? (
                      <span className="text-slate-500">{row.status}</span>
                    ) : row.download_path ? (
                      <a href={reportFileUrl(row.id)} className="text-slate-200 hover:text-white">
                        {t('rep.download')}
                      </a>
                    ) : (
                      <span className="text-loss">{row.error ?? row.status}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}

function FormatSelect({
  value,
  onChange,
}: {
  value: 'xlsx' | 'pdf' | 'csv'
  onChange: (value: 'xlsx' | 'pdf' | 'csv') => void
}) {
  return (
    <label className="text-xs text-slate-500">
      {t('rep.format')}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as 'xlsx' | 'pdf' | 'csv')}
        className="mt-1 block rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
      >
        <option value="xlsx">XLSX</option>
        <option value="pdf">PDF</option>
        <option value="csv">CSV</option>
      </select>
    </label>
  )
}

function openFile(row: ReportRunOut) {
  if (!row.download_path) return
  window.location.assign(reportFileUrl(row.id))
}
